<template>
    <div class="py-6 px-1 bg-white border border-black" :id="`edit${address.id}`">
        <delivery-address-form
            v-model="deliveryAddress">
        </delivery-address-form>

        <delivery-instructions-field
            v-if="isDEU"
            v-model="newDeliveryComment"
            :require="isDEU">
        </delivery-instructions-field>

        <delivery-address-property-type-field
            :require="true" v-model="newPropertyType">
        </delivery-address-property-type-field>

        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error list-disc text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"></span>:
                    <span class="font-normal" v-text="$t(errorMsg)"></span>
                </li>
            </ul>
        </div>
        <div class="w-full md:flex pt-4 pb-2 px-2">
            <div class="w-full md:w-1/2 px-0 md:px-1 py-1 md:py-0">
                <button
                    class="w-1/2 btn btn--secondary btn--auto-width btn--full-width"
                    @click="close" v-text="$t('BUTTON_CANCEL')"></button>
            </div>
            <div class="w-full md:w-1/2 px-0 md:px-1 py-1 md:py-0">
                <submit-button
                    class="btn--full-width"
                    @click="submit"
                    :hoverable="false"
                    :processing="formProcessing" v-text="$t('BUTTON_UPDATE')"></submit-button>
            </div>
        </div>
    </div>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import requiredIf from 'vuelidate/src/validators/requiredIf';
import { mapGetters } from 'vuex';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import AddressService from '@/services/logistics/AddressService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import DeliveryInstructionsField from '@/components/forms/fields/DeliveryInstructionsField';
import DeliveryAddressForm from '@/components/forms/addresses/DeliveryAddressForm';
import DeliveryAddressPropertyTypeField from '@/components/forms/fields/LegacyDeliveryAddressPropertyTypeField';

export default {
    name: 'EditDeliveryAddressForm',
    data() {
        return {
            mappedAddress: {
                houseNumber: null,
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
                state: null,
            },
            newDeliveryComment: null,
            newPropertyType: null,
            errorHandler: new ErrorResponseHandler(),
            formProcessing: false,
        };
    },
    props: {
        address: {
            required: true,
            type: Object,
        },
        propertyType: {
            required: true,
            type: String,
        },
        deliveryComment: {
            required: true,
            type: String,
        },
        subscriptionId: {
            required: true,
            type: Number,
        },
    },
    created() {
        this.mapAddressForAddressForm(this.address);

        this.newDeliveryComment = this.deliveryComment;
        this.newPropertyType = this.propertyType;
    },
    methods: {
        close() {
            this.$emit('closed');
        },

        submit() {
            this.toggleFormProcessing();
            this.validateInput();
        },

        handleUpdatingAddressViaApi() {
            let newAddress = {
                firstName: this.address.first_name,
                lastName: this.address.last_name,
                houseNumber: this.mappedAddress.houseNumber,
                address1: this.mappedAddress.addressLine1,
                address2: this.mappedAddress.addressLine2,
                city: this.mappedAddress.city,
                country: this.address.country,
                postcode: this.mappedAddress.postcode,
                deliveryComment: this.newDeliveryComment,
                propertyType: this.newPropertyType,
                state: this.mappedAddress.state,
            };

            let service = new AddressService();
            service.updateSubscriptionAddress(this.subscriptionId, this.address.id, newAddress)
                .then(() => {
                    setTimeout(() => {
                        this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                        this.$store.dispatch('address/fetchAllAddresses', true);
                    }, 1000);

                    this.$scrollTo('.main');
                    this.$emit('subscription-address-updated');
                    this.toggleFormProcessing();
                    this.close();
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response.data);
                    this.toggleFormProcessing();
                });
        },

        mapAddressForAddressForm(address) {
            this.mappedAddress.houseNumber = address.houseNumber;
            this.mappedAddress.addressLine1 = address.address1;
            this.mappedAddress.addressLine2 = address.address2;
            this.mappedAddress.city = address.city;
            this.mappedAddress.postcode = address.postcode;
            this.mappedAddress.state = address.state;
        },

        validateInput() {
            this.$v.$touch();

            this.areAllFormFieldsValid ? this.handleUpdatingAddressViaApi() : this.forceRequiredValidationMessagesVisibility();
        },

        forceRequiredValidationMessagesVisibility() {
            if (this.mappedAddress.houseNumber === null) this.mappedAddress.houseNumber = '';
            if (this.mappedAddress.addressLine1 === null) this.mappedAddress.addressLine1 = '';
            if (this.mappedAddress.city === null) this.mappedAddress.city = '';
            if (this.mappedAddress.postcode === null) this.mappedAddress.postcode = '';
            if (this.newDeliveryComment === null) this.newDeliveryComment = '';
            if (this.newPropertyType === null) this.newPropertyType = '';

            this.$scrollTo(`#edit${this.address.id}`);
            this.toggleFormProcessing();
        },

        toggleFormProcessing() {
            this.formProcessing = !this.formProcessing;
        },
    },
    computed: {
        ...mapGetters({
            isDEU: 'localisation/isDEU',
            isUSA: 'localisation/isUSA',
        }),
        areAllFormFieldsValid() {
            return !this.$v.$invalid;
        },
        deliveryAddress: {
            get() {
                return this.mappedAddress;
            },
            set(address) {
                this.mappedAddress.houseNumber = address.houseNumber;
                this.mappedAddress.addressLine1 = address.addressLine1;
                this.mappedAddress.addressLine2 = address.addressLine2;
                this.mappedAddress.city = address.city;
                this.mappedAddress.postcode = address.postcode;
                this.mappedAddress.state = address.state;
            },
        },
    },
    validations: {
        mappedAddress: {
            houseNumber: {
                required: requiredIf('!isUSA'),
            },
            addressLine1: {
                required,
            },
            city: {
                required,
            },
            postcode: {
                required,
            },
        },
        newPropertyType: {
            required,
        },
        newDeliveryComment: {
            required: requiredIf('isDEU'),
        },
    },
    components: {
        DeliveryAddressForm,
        DeliveryInstructionsField,
        DeliveryAddressPropertyTypeField,
        SubmitButton,
    },
};
</script>
